import React, { createContext, useContext, useMemo } from 'react'

import {
  shouldCheckLiveOnV2,
  type RedirectCommonProps,
} from './should-check-live-on-v2'

/**
 * Whether the current caller should have 1.0 task UI gated on live-on-2.0
 * state. False by default so a tree without a provider behaves as plain 1.0,
 * the same fail-open direction as the rest of this folder.
 */
const LiveOnV2CheckContext = createContext<boolean>(false)

interface LiveOnV2CheckProviderProps {
  /** The page's server-evaluated common props: session, org user, treatments. */
  commonProps: RedirectCommonProps
  children: React.ReactNode
}

/**
 * Makes the page-level `shouldCheckLiveOnV2` answer available anywhere in a
 * Pages Router tree. The global task list passes that answer down as one prop
 * (`shouldCheckMigratedProjects`); this is for pages whose gated UI sits
 * several components deep, such as the capacity planning modal, where
 * threading a prop through components that never read it is the wrong shape.
 *
 * Mount it once in `_app` with the common props every page already returns.
 * A page that returns no treatments reads the flag as off and makes no lookup.
 */
export function LiveOnV2CheckProvider({
  commonProps,
  children,
}: LiveOnV2CheckProviderProps) {
  const { sessionData, userOrgData, treatments } = commonProps
  const shouldCheck = useMemo(
    () => shouldCheckLiveOnV2({ sessionData, userOrgData, treatments }),
    [sessionData, userOrgData, treatments],
  )

  return (
    <LiveOnV2CheckContext.Provider value={shouldCheck}>
      {children}
    </LiveOnV2CheckContext.Provider>
  )
}

/**
 * The `enabled` argument for `useLiveOnV2Tasks`: true when the migration flag
 * is on and the caller is not a workspace admin or super admin.
 */
export function useShouldCheckLiveOnV2(): boolean {
  return useContext(LiveOnV2CheckContext)
}
